// apps/web/src/features/russia-requests/components/editor/RussiaRequestEditLoader.tsx
//
// Edit-mode loader for the russia-rates editor (#1571). Mounts at
// `/russia-rates/:id/edit`, loads the existing request, and prefills the
// shared `RussiaEditorShell` so a Manager can complete an incomplete
// DRAFT / RETURN draft (set COMPLEX totals, or add/edit SIMPLE positions)
// and then send it. Only DRAFT / RETURN rows are editable here — any other
// status (or a 404/403) redirects back to the read-only drawer; the
// backend gate remains the security boundary.

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  RussiaRequestType,
  isDraftLikeRequestStatus,
  type CreateRussiaRequestManualInput,
  type RussiaRequest,
} from '@magimp/shared';
import { EditorDrawer } from '../../../../ui/index.js';
import { useRussiaRequest } from '../../queries.js';
import { RussiaRequestDrawerLoadingShell } from '../RussiaRequestDrawerLoadingShell.js';
import { RussiaRequestDrawerUnavailableStub } from '../RussiaRequestDrawerUnavailableStub.js';
import { RussiaEditorShell } from './RussiaEditorShell.js';
import { seededPositionsFromRequest } from './russiaPositions.model.js';

export function RussiaRequestEditLoader({ id }: { id: string }) {
  const navigate = useNavigate();
  const drawerPath = `/russia-rates/${encodeURIComponent(id)}`;
  const query = useRussiaRequest(id);
  const data = query.data;

  // A non-editable status (or a successfully loaded row that is not
  //   DRAFT/RETURN) has no edit affordance — bounce to the read-only drawer.
  //   Effect, not render-time navigate, so React Router commits cleanly.
  const editable = data !== undefined && isDraftLikeRequestStatus(data.status);
  useEffect(() => {
    if (data !== undefined && !editable) {
      navigate(drawerPath, { replace: true });
    }
  }, [data, editable, navigate, drawerPath]);

  if (query.isError) {
    return (
      <EditorDrawer
        dirty={false}
        onConfirmClose={() => navigate('/russia-rates', { replace: true })}
        width={640}
        narrow={false}
      >
        <RussiaRequestDrawerUnavailableStub
          requestId={id}
          onClose={() => navigate('/russia-rates', { replace: true })}
        />
      </EditorDrawer>
    );
  }

  if (!data || !editable) {
    return (
      <EditorDrawer
        dirty={false}
        onConfirmClose={() => navigate(drawerPath, { replace: true })}
        width={640}
        narrow={false}
      >
        <RussiaRequestDrawerLoadingShell id={id} onClose={() => navigate(drawerPath)} />
      </EditorDrawer>
    );
  }

  return <RussiaRequestEditShell request={data} />;
}

function RussiaRequestEditShell({ request }: { request: RussiaRequest }) {
  // Build the form defaults + seeded positions from the loaded row. The shell
  //   is keyed by `request.id` below, so RHF reads these defaults only at mount
  //   and re-mounts cleanly if the route id changes — a background refetch that
  //   yields a new `request` object never resets the user's in-progress edits.
  const defaults = useMemo<CreateRussiaRequestManualInput>(
    () => ({
      // `type` is not edited here but the create schema requires it; carry the
      //   loaded value verbatim so the PATCH body round-trips it unchanged.
      type: request.type as RussiaRequestType,
      kind: request.kind,
      name: request.name,
      endAddress: request.endAddress,
      startCityIds: [...request.startCities]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((c) => c.arrivalCityId),
      totalWeight: request.totalWeight,
      totalVolume: request.totalVolume,
    }),
    [request],
  );

  const initialPositions = useMemo(() => seededPositionsFromRequest(request), [request]);

  return (
    <RussiaEditorShell
      key={request.id}
      kind={request.kind}
      defaults={defaults}
      initialPersistedId={request.id}
      initialPositions={initialPositions}
      editMode
    />
  );
}
